import type { StockMovementRequest } from '@agos/api-client/model'

import { tt } from '@agos/shared'

import { costUnitOf, isKnownUnit, type StockItem } from './stock-levels'

/**
 * The bodies `POST /stock/items`, `PATCH /stock/items/{id}` and
 * `POST /stock/movements` want.
 *
 * All three are `{ "type": "object", "additionalProperties": {} }` in the spec —
 * springdoc saw a `Map<String, Object>` — so orval generates no request type and
 * nothing checks these at compile time. That is precisely why they are built here,
 * under test, rather than inline in a form's submit handler.
 */

export interface ItemForm {
  name: string
  type: string
  category: string
  unit: string
  qty: string
  minQty: string
  /** A per-unit purchase price. Optional, and admin-only in the UI. */
  costPrice: string
  costUnit: string
  defaultSupplierId: string
  active: boolean
}

export function formOf(item: StockItem | null): ItemForm {
  return {
    name: item?.name ?? '',
    type: item?.type ?? 'raw_material',
    category: item?.category ?? '',
    unit: item?.unit ?? 'ks',
    qty: item?.qty === undefined ? '' : String(item.qty),
    minQty: item?.minQty === undefined ? '' : String(item.minQty),
    costPrice: item?.costPrice == null ? '' : String(item.costPrice),
    // FB-077: the cost unit defaults to the item's own, which for four items in the
    // data is a unit the ten-option list does not contain.
    costUnit: item ? costUnitOf(item) : '',
    defaultSupplierId: item?.defaultSupplierId == null ? '' : String(item.defaultSupplierId),
    active: item?.active ?? true,
  }
}

/** A quantity, not money — fractional by nature (8,25 kg of flour). */
function quantity(raw: string): number {
  const parsed = Number(raw.trim().replace(',', '.'))
  return Number.isFinite(parsed) ? parsed : 0
}

/** A per-unit price keeps every decimal it was given (FB-056). */
function rate(raw: string): number | null {
  const trimmed = raw.trim()
  if (trimmed === '') return null
  const parsed = Number(trimmed.replace(',', '.'))
  return Number.isFinite(parsed) ? parsed : null
}

export type FormProblem =
  | { kind: 'no-name' }
  | { kind: 'no-unit' }
  | { kind: 'negative-qty' }
  /** The cost unit is not the unit the item is counted in. */
  | { kind: 'cost-unit-mismatch'; unit: string; costUnit: string }

/**
 * What is wrong with the form — or `null`.
 *
 * `cost-unit-mismatch` is a warning, not a refusal: a price per carton against a
 * quantity in pieces is legitimate if somebody knows the conversion, and one item in
 * the data is deliberately `Liter`/`l`. But it is also what FB-077 produces silently,
 * so it is said out loud.
 */
export function checkForm(form: ItemForm): FormProblem | null {
  if (form.name.trim() === '') return { kind: 'no-name' }
  if (form.unit.trim() === '') return { kind: 'no-unit' }
  if (quantity(form.qty) < 0 || quantity(form.minQty) < 0) return { kind: 'negative-qty' }

  const costUnit = form.costUnit.trim()
  if (rate(form.costPrice) !== null && costUnit !== '' && costUnit !== form.unit.trim()) {
    return { kind: 'cost-unit-mismatch', unit: form.unit.trim(), costUnit }
  }
  return null
}

/** Whether the form may be submitted. A unit mismatch may. */
export function canSave(problem: FormProblem | null): boolean {
  return problem === null || problem.kind === 'cost-unit-mismatch'
}

export function itemPayload(form: ItemForm): Record<string, unknown> {
  const costPrice = rate(form.costPrice)
  const supplierId = form.defaultSupplierId.trim()

  return {
    name: form.name.trim(),
    type: form.type,
    category: form.category.trim(),
    unit: form.unit.trim(),
    qty: quantity(form.qty),
    minQty: quantity(form.minQty),
    active: form.active,
    // Sent as null rather than omitted, so clearing a price clears it. Omitting the
    // key leaves the stored value alone — the same trap FB-051 named.
    costPrice,
    costUnit: costPrice === null ? null : form.costUnit.trim() || form.unit.trim(),
    defaultSupplierId: supplierId === '' ? null : Number(supplierId),
  }
}

export type MovementKind = 'in' | 'out' | 'adjustment' | 'waste'

export interface MovementForm {
  stockItemId: number
  type: MovementKind
  qty: string
  note: string
  reference: string
}

/**
 * `StockMovementRequest` now that the endpoint declares one — it took a bare map before, so nothing
 * checked this at compile time.
 *
 * `note` and `reference` are omitted rather than sent as `null`: the record's `asMap` drops a null, so
 * sending one only to have it dropped is a round trip that says nothing.
 */
export function movementPayload(form: MovementForm): StockMovementRequest {
  const note = form.note.trim()
  const reference = form.reference.trim()
  return {
    stockItemId: form.stockItemId,
    type: form.type,
    // Always positive on the wire; the direction is the type. A correction may be
    // negative, and is the only kind that may.
    qty: form.type === 'adjustment' ? quantity(form.qty) : Math.abs(quantity(form.qty)),
    ...(note === '' ? {} : { note }),
    ...(reference === '' ? {} : { reference }),
  }
}

/** Whether a movement can be posted at all. */
export function canPost(form: MovementForm): boolean {
  const qty = quantity(form.qty)
  if (form.type === 'adjustment') return qty !== 0
  return qty > 0
}

/**
 * What a movement will leave in stock — shown before it is posted.
 *
 * Legacy posts and then shows the new figure. Taking 30 kg out of 4 kg is a thing
 * somebody should see before pressing, not after.
 */
export function resultingQty(item: StockItem, form: MovementForm): number {
  const current = item.qty ?? 0
  const qty = quantity(form.qty)
  if (form.type === 'in') return current + Math.abs(qty)
  if (form.type === 'adjustment') return current + qty
  return current - Math.abs(qty)
}

export function overdrawWarning(item: StockItem, form: MovementForm): string | null {
  if (form.type === 'in') return null
  if (resultingQty(item, form) >= 0) return null
  return tt('Po tomto pohybe by bol stav zásoby negatívny.')
}

/** A unit the ten-option list does not have, worth saying once in the editor. */
export function customUnitNote(unit: string): string | null {
  if (unit.trim() === '' || isKnownUnit(unit.trim())) return null
  return tt('Táto jednotka nie je v zozname - zostane zachovaná tak, ako je uložená.')
}
